


































































import {
  computed,
  defineComponent,
  PropType,
  ref,
  watch
} from '@vue/composition-api';
import {
  useCreateBankPaymentInstruction,
  useUpdateBankPaymentInstruction
} from '@/composable/bank';
import { AxiosError } from 'axios';

import { cloneDeep } from 'lodash';
import { diffDeep, setErrorFromAxiosError } from '@/utils/helpers';
import {
  IBankPaymentInstruction,
  IBankPaymentInstructionData,
  BankPaymentInstruction
} from '@/models';
import { ISystemError } from '@/models/error/error.types';
import { ALForm } from '@/common/component.types';

import ErrorAlert from '@/components/common/ErrorAlert.vue';
import ALDialog from '@/components/core/Dialog/ALDialog.vue';

export default defineComponent({
  name: 'PaymentInstructionDialog',
  components: {
    ALDialog,
    ErrorAlert
  },
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    paymentInstruction: {
      type: Object as PropType<IBankPaymentInstructionData | null>,
      default: null
    },
    bankId: Number
  },
  emits: ['update:dialog'],
  setup(props, { emit, root }) {
    const computedBankId = computed(() => props.bankId!);

    const localDialog = computed({
      get: () => props.dialog,
      set: value => {
        emit('update:dialog', value);
      }
    });

    const paymentInstructionData = ref<IBankPaymentInstructionData>({
      bank: computedBankId.value
    });

    watch(localDialog, value => {
      if (value) {
        resetFormValidation();
        paymentInstructionData.value = !props.isEdit
          ? ({ bank: computedBankId.value } as IBankPaymentInstructionData)
          : cloneDeep(props.paymentInstruction!);
      }
    });

    const formRef = ref<ALForm | null>(null);
    const valid = ref(false);

    const subtitle = computed(
      () =>
        `${props.isEdit ? 'Update' : 'Create'} payment instruction information`
    );

    function closeDialog() {
      clearErrors();
      localDialog.value = false;
    }

    const {
      isLoading: isUpdatePending,
      error: updateError,
      mutate: updatePaymentInstruction,
      reset: updateReset
    } = useUpdateBankPaymentInstruction();
    const {
      isLoading: isCreatePending,
      error: createError,
      mutate: createPaymentInstruction,
      reset: createReset
    } = useCreateBankPaymentInstruction();

    const localError = ref<ISystemError | null>(null);

    const loading = computed(() =>
      props.isEdit ? isUpdatePending.value : isCreatePending.value
    );

    const error = computed<ISystemError | null>({
      get() {
        let systemError: ISystemError | null = localError.value;
        const axiosError = props.isEdit ? updateError.value : createError.value;

        if (axiosError) {
          systemError = setErrorFromAxiosError(axiosError as AxiosError);
        }

        return systemError;
      },
      set(value: ISystemError | null) {
        localError.value = value;
      }
    });

    function clearErrors() {
      props.isEdit ? updateReset() : createReset();
      error.value = null;
    }

    function resetFormValidation(): void {
      if (formRef.value) {
        return formRef.value.resetValidation();
      }
    }

    function validateForm(): boolean {
      let valid = false;
      if (formRef.value) {
        valid = formRef.value.validate();
      }
      return valid;
    }

    async function save() {
      clearErrors();
      const valid = await validateForm();

      if (valid) {
        let paymentInstructionInstance: IBankPaymentInstruction | null = null;

        try {
          paymentInstructionInstance = new BankPaymentInstruction(
            paymentInstructionData.value
          );
        } catch (e) {
          // When constructing the instance before saving the possibility of an error can occur the local error is to store that error instance and display it
          error.value = e as ISystemError;
        }

        if (paymentInstructionInstance != null) {
          const saveAction = props.isEdit
            ? updatePaymentInstruction
            : createPaymentInstruction;

          let payload = paymentInstructionInstance;
          if (props.isEdit) {
            payload = diffDeep(
              paymentInstructionInstance,
              props.paymentInstruction,
              true
            );
          }

          saveAction(payload, {
            onSuccess: () => {
              closeDialog();
            }
          });
        }
      }
    }

    return {
      localDialog,
      paymentInstructionData,
      valid,
      formRef,
      subtitle,
      closeDialog,
      save,
      clearErrors,
      loading,
      error
    };
  }
});
